import React, { useCallback, useState } from 'react';
import { Grid, Theme } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';

import clsx from 'clsx';
import {
  FormColorPicker,
  ImagePreview,
  FormInput,
  CollapsableSection,
  SectionTitle,
  FormUrlInput,
  Button,
} from '../../components';
import yup from '../../utility/yup';
import { FormRenderProps } from '../../components/item-form/ItemForm';
import { VideoMenu } from '../../types/items';
import { TabComponentProps } from '../../components/tabs/Tabs';
import Preview from './Preview';
import { LinkIcon, UploadIcon } from '../../components/icon/solid';

export const formatSchema = {
  'options.colors_controls_base': yup.string().color(),
  'options.colors_controls_accent': yup.string().color(),
  'options.colors_button_background_active': yup.string().color(),
  'options.colors_button_background_inactive': yup.string().color(),
  'options.colors_button_foreground': yup.string().color(),
  'options.body_font': yup.string(),
  'options.button_classes': yup.string(),
  'options.button_style': yup.string(),
  'options.logo_url': yup.string().url(),
  'options.logo_style': yup.string(),
};

type Props = FormRenderProps<VideoMenu> & TabComponentProps;

const useStyles = makeStyles(({ palette }: Theme) => createStyles({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  colorContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  logoButton: {
    textTransform: 'none',
    marginRight: '16px',
    width: '120px',
    height: '32px',
    border: `1px solid ${palette.buttonSingle.color}`,
    color: palette.buttonSingle.color,
    padding: '3px 15px',
    '&:hover': {
      backgroundColor: palette.buttonSingle.hover,
    },
  },
  logoButtonActive: {
    border: 'none',
    color: palette.common.white,
    backgroundColor: palette.buttonSingle.color,
    '&:hover': {
      backgroundColor: palette.buttonSingle.color,
    },
  },
  logoButtonIcon: {
    width: '16px',
    height: '16px',
    marginRight: '8px',
  },
}), { name: 'TabFormat' });

const TabFormat = ({ active, ...props }: Props) => {
  const classes = useStyles();
  const {
    values: {
      options: {
        colors_controls_base: controlsBase = '',
        colors_controls_accent: controlsAccent = '',
        colors_button_background_active: buttonBgActive = '',
        colors_button_background_inactive: buttonBgInactive = '',
        colors_button_foreground: buttonFg = '',
        button_style: buttonStyle = '',
        logo_url: logoUrl = '',
        logo_style: logoStyle = '',
      } = {},
    } = {},
  } = props;

  const [logoMode, setLogoMode] = useState<'upload' | 'link'>('link');
  const logoUpload = useCallback(() => setLogoMode('upload'), [setLogoMode]);
  const logoLink = useCallback(() => setLogoMode('link'), [setLogoMode]);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <SectionTitle>Colors</SectionTitle>
      </Grid>
      <Grid item xs={12} className={classes.container}>
        <Grid item xs={6} className={classes.colorContainer}>
          <Grid item xs={12}>
            <FormColorPicker
              label="Video player base color"
              name="options.colors_controls_base"
            />
          </Grid>
          <Grid item xs={12}>
            <FormColorPicker
              label="Video player accent color"
              name="options.colors_controls_accent"
            />
          </Grid>
          <Grid item xs={12}>
            <FormColorPicker
              label="Button active color"
              name="options.colors_button_background_active"
            />
          </Grid>
          <Grid item xs={12}>
            <FormColorPicker
              label="Button inactive color"
              name="options.colors_button_background_inactive"
            />
          </Grid>
          <Grid item xs={12}>
            <FormColorPicker
              label="Button foreground color"
              name="options.colors_button_foreground"
            />
          </Grid>
        </Grid>
        <Grid item xs={5}>
          <Preview
            buttonBgActive={buttonBgActive}
            buttonBgInactive={buttonBgInactive}
            buttonFg={buttonFg}
            playerBaseColor={controlsBase}
            playerAccentColor={controlsAccent}
            buttonStyle={buttonStyle}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>Logo</SectionTitle>
      </Grid>
      <Grid item container xs={12} spacing={4}>
        <Grid item container xs={8} spacing={2}>
          <Grid item xs={12}>
            <Button
              overlay={logoMode === 'upload'}
              className={clsx(classes.logoButton, {
                [classes.logoButtonActive]: logoMode === 'upload',
              })}
              onClick={logoUpload}
            >
              <UploadIcon className={classes.logoButtonIcon} />
              Upload
            </Button>
            <Button
              overlay={logoMode === 'link'}
              className={clsx(classes.logoButton, {
                [classes.logoButtonActive]: logoMode === 'link',
              })}
              onClick={logoLink}
            >
              <LinkIcon className={classes.logoButtonIcon} />
              Link
            </Button>
          </Grid>
          <Grid item xs={12}>
            {logoMode === 'link' && (
              <FormUrlInput
                label="Logo URL"
                name="options.logo_url"
                spellCheck
              />
            )}
            {logoMode === 'upload' && (
              <div>Upload is not supported yet.</div>
            )}
          </Grid>
        </Grid>
        <Grid item container xs={4} alignItems="center">
          <ImagePreview src={logoUrl} styles={logoStyle} />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <CollapsableSection title="Advanced">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormInput
                label="Font"
                name="options.body_font"
                spellCheck
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                label="Additional button classes"
                name="options.button_classes"
                spellCheck
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                label="Additional button style"
                name="options.button_style"
                spellCheck
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                label="Additional logo style"
                name="options.logo_style"
                spellCheck
              />
            </Grid>
          </Grid>
        </CollapsableSection>
      </Grid>
    </Grid>
  );
};

export default TabFormat;
